import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import RouteGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/route-group/route-group.entity";
import { ICreateRouteGroup } from "./interfaces/create-route-group.interface";
import { IUpdateRouteGroup } from "./interfaces/update-route-group.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";
const apiUrl = env.API_ROUTING_URL;
const moduleName = "route_group";

@Injectable({
  providedIn: "root",
})
export class RouteGroupService extends AbstractService<
  RouteGroupAttributes,
  ICreateRouteGroup,
  IUpdateRouteGroup
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<RouteGroupAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<RouteGroupAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      const headers: Record<string, any> = {};
      // see order-customer-export for more info about this header
      // ctx: stands for context
      if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
      return this.http.get<IPaginationResult<RouteGroupAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene grupos de rutas con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'id', 'name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: id, name
   * Array filters: status
   * Single filters: companyId
   */
  findAllWithCount(
    options: IPagination<RouteGroupAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<RouteGroupAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<RouteGroupAttributes>[] => {
      const deletedFilter: FilterFn<RouteGroupAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof RouteGroupAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por ID (o solo condiciones si no hay búsqueda)
    const searchByIdQueryObject: FilterFn<RouteGroupAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<RouteGroupAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<RouteGroupAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByIdQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<RouteGroupAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<RouteGroupAttributes[]>> {
    const query = buildQuery<RouteGroupAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["_id","id", "name"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<RouteGroupAttributes[]>>(API_URL);
  }

  findByCompanyId(companyId: string): Observable<IRequestResult<RouteGroupAttributes[]>> {
    const query = buildQuery<RouteGroupAttributes>(
      where("company._id" as keyof RouteGroupAttributes, Ops.eq(companyId)),
      andWhere("statusId", Ops.eq(StatusEnum.ACTIVE)),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<RouteGroupAttributes[]>>(API_URL);

  }
}
